// @sympllex/auth — RBAC permission codes + effective-permission resolution.
// Source: B3-RBAC-CATALOG §1 (10 standard roles), §2 (permission namespace + verbs
// + §2.5 resolution). The route gate (B5B §2.3) calls hasPermission(session, code);
// gating is UI-affordance only — row-level access is enforced server-side by RLS
// (B5B §2.4), never by the client.
import type { MandateScope, SympllexSession } from "./session";

// ---------------- §2.2 aggregate prefixes (22 aggregates from B2; +bestandssysteem PLAN-W27 B) ----------------
export const AGGREGATES = [
  "verwerking", "verwerking_doel", "verwerking_snapshot", "bedrijfsmiddel", "bedreiging", "bestandssysteem",
  "risico", "security_measure_instance", "ticket", "ticket_dependency", "bewijscontainer",
  "data_subject_request", "partner_relationship", "implementation_program", "sprint_gate_decision",
  "pdca_schedule", "change_trigger", "application_regulation", "biv_impact", "audit_log",
  "tenant_user", "tenant_settings", "tenant_billing", "notification", "transfer_impact_assessment",
  // W27-A — Beleidsmaker singletons (beleidsdocument: Missie/Gedragsregels/Hoofddoelplanning).
  // Deliberately NOT in TENANT_AGGREGATES: the singleton surface has only read + update
  // (no create/delete/archive — the page IS the entity; upsert is an update semantically).
  "policy_document",
  // W29 — Organisatie surfaces: establishments register + privacy-team roster (tenant-scoped).
  "vestiging", "privacyteam_membership",
  // W30 — Planner · Taken (tasks).
  "activity_task",
] as const;
export type Aggregate = (typeof AGGREGATES)[number];

// reserved namespaces (§2.2): super_tenant.* firm.* partner.* system.*
export const NAMESPACES = ["super_tenant", "firm", "partner", "system"] as const;

// ---------------- §2.3 standard verbs ----------------
export const VERBS = [
  "read", "write", "create", "update", "delete", "archive", "restore", "assign", "sign_off",
  "export", "download", "upload", "invite_user", "revoke_user", "subscribe", "cancel",
  "approve", "reject", "impersonate",
] as const;
export type Verb = (typeof VERBS)[number];

/** A permission code in `<aggregate>.<verb>[.<modifier>]` form (§2.1). */
export type PermissionCode = string;

/** Build a permission code (§2.1). */
export function permissionCode(aggregate: string, verb: Verb, modifier?: string): PermissionCode {
  return modifier ? `${aggregate}.${verb}.${modifier}` : `${aggregate}.${verb}`;
}

// ---------------- §1.1 the 10 standard roles ----------------
export const STANDARD_ROLES = [
  "super_tenant_admin", "super_tenant_support", "super_tenant_compliance",
  "controller_tenant_admin", "controller_tenant_user",
  "processor_tenant_admin", "processor_tenant_user",
  "partner_tenant_primary_contact", "partner_tenant_consultant", "partner_tenant_billing_contact",
] as const;
export type StandardRole = (typeof STANDARD_ROLES)[number];

const TENANT_AGGREGATES: readonly string[] = [
  "verwerking", "verwerking_doel", "bedrijfsmiddel", "bedreiging", "bestandssysteem", "risico",
  "security_measure_instance", "ticket", "ticket_dependency", "bewijscontainer",
  "data_subject_request", "implementation_program", "sprint_gate_decision",
  "pdca_schedule", "change_trigger", "application_regulation", "biv_impact", "notification",
  "transfer_impact_assessment", "vestiging", "privacyteam_membership", "activity_task",
];
const rw = (aggs: readonly string[], verbs: Verb[]) => aggs.flatMap((a) => verbs.map((v) => permissionCode(a, v)));

/**
 * Build-time role→permission index (B3-RBAC-CATALOG §2.5 says this is codegen'd from
 * `admin-functies-library`; until that pipeline exists, this is the canonical seed for
 * the 10 standard roles). Tenants may add overrides/custom roles at runtime (§3) —
 * those resolve from the DB and are merged onto whatever the session carries.
 */
export const ROLE_PERMISSIONS: Record<StandardRole, PermissionCode[]> = {
  super_tenant_admin: ["*"], // full platform admin (§1.1 #1) — superset
  super_tenant_support: [
    "super_tenant.impersonate_tenant",
    ...rw(TENANT_AGGREGATES, ["read"]),
    "audit_log.read", "tenant_user.read", "tenant_settings.read", "tenant_billing.read",
  ],
  super_tenant_compliance: [
    "super_tenant.security_audit.read", "audit_log.read", "audit_log.export",
    ...rw(TENANT_AGGREGATES, ["read"]),
  ],
  controller_tenant_admin: [
    ...rw(TENANT_AGGREGATES, ["read", "write", "create", "update", "delete", "archive", "restore"]),
    "ticket.assign", "sprint_gate_decision.sign_off", "bewijscontainer.download", "bewijscontainer.upload",
    // A controller manages its Art.28 processor relationships (DPAs, sub-processors, shared engagements).
    // Full CRUD — update/delete were missing, which 403'd the Externe-partijen Bewerken/Deactiveren (Phase D).
    "partner_relationship.read", "partner_relationship.write", "partner_relationship.update", "partner_relationship.delete",
    // W27-A — Beleidsmaker singletons: read + update only (the singleton has no create/delete surface).
    "policy_document.read", "policy_document.update",
    "tenant.invite_user", "tenant_user.read", "tenant_user.revoke_user",
    "tenant_settings.read", "tenant_settings.update", "tenant_billing.read", "billing.subscribe", "billing.cancel",
    "audit_log.read", "audit_log.export",
  ],
  controller_tenant_user: [
    ...rw(TENANT_AGGREGATES, ["read", "write", "create", "update"]),
    "ticket.assign", "bewijscontainer.download", "bewijscontainer.upload",
    "partner_relationship.read", "partner_relationship.write", "partner_relationship.update",
    // W27-A — Beleidsmaker singletons (policy authoring is a controller-tenant activity).
    "policy_document.read", "policy_document.update",
  ],
  processor_tenant_admin: [
    ...rw(TENANT_AGGREGATES, ["read", "write", "create", "update", "delete", "archive", "restore"]),
    "ticket.assign", "sprint_gate_decision.sign_off", "bewijscontainer.download", "bewijscontainer.upload",
    "partner_relationship.read", "partner_relationship.write", "partner_relationship.update", "partner_relationship.delete",
    "tenant.invite_user", "tenant_user.read", "tenant_user.revoke_user",
    "tenant_settings.read", "tenant_settings.update", "tenant_billing.read", "billing.subscribe", "billing.cancel",
    "audit_log.read", "audit_log.export",
  ],
  processor_tenant_user: [
    ...rw(TENANT_AGGREGATES, ["read", "write", "create", "update"]),
    "ticket.assign", "bewijscontainer.download", "bewijscontainer.upload",
    "partner_relationship.read",
  ],
  partner_tenant_primary_contact: [
    "firm.read", "firm.write", "firm.invite_user", "firm.revoke_user",
    "partner_relationship.read", "partner_relationship.write", "partner_relationship.update", "partner_relationship.delete",
    "partner.switch_tenant_context", "verwerking.read.cross_tenant",
  ],
  partner_tenant_consultant: [
    "partner.switch_tenant_context",
    ...rw(TENANT_AGGREGATES, ["read", "write", "create", "update"]),
    "ticket.assign", "bewijscontainer.download", "bewijscontainer.upload",
    "partner_relationship.read",
  ],
  partner_tenant_billing_contact: [
    "firm.read", "partner_relationship.read", "tenant_billing.read", "billing.subscribe", "billing.cancel",
  ],
};

/**
 * §2.5 effective-permission resolution. Unions the permission codes for the session's
 * roles. If the session carries an explicit `permissionCodes` set (DB-resolved, incl.
 * custom roles/overrides), that takes precedence. Partner-consultant sessions with a
 * mandate scope are intersected against its action allow-list.
 */
export function getEffectivePermissions(
  session: Pick<SympllexSession, "roleCodes"> & { permissionCodes?: string[]; mandateScope?: MandateScope; actingAsPartnerUserId?: string },
): Set<PermissionCode> {
  const codes = new Set<PermissionCode>();
  if (session.permissionCodes?.length) {
    session.permissionCodes.forEach((c) => codes.add(c));
  } else {
    for (const role of session.roleCodes) {
      const perms = ROLE_PERMISSIONS[role as StandardRole];
      if (perms) perms.forEach((p) => codes.add(p));
    }
  }
  if (session.actingAsPartnerUserId && session.mandateScope) {
    return intersectWithMandateScope(codes, session.mandateScope);
  }
  return codes;
}

/** Keep only codes whose verb is in the mandate's action allow-list (when present). */
export function intersectWithMandateScope(codes: Set<PermissionCode>, scope: MandateScope): Set<PermissionCode> {
  if (!scope.actions?.length) return codes;
  const allowed = new Set(scope.actions);
  const out = new Set<PermissionCode>();
  for (const c of codes) {
    if (c === "*") { out.add(c); continue; }
    const verb = c.split(".")[1];
    if (verb && allowed.has(verb)) out.add(c);
  }
  return out;
}

/**
 * Route-gate primitive (B5B §2.3). True if the session may exercise `code`. Honours the
 * `*` superuser wildcard and prefix wildcards (e.g. `super_tenant.*`). Exact match else.
 */
export function hasPermission(
  session: Pick<SympllexSession, "roleCodes"> & { permissionCodes?: string[]; mandateScope?: MandateScope; actingAsPartnerUserId?: string },
  code: PermissionCode,
): boolean {
  const eff = getEffectivePermissions(session);
  if (eff.has("*")) return true;
  if (eff.has(code)) return true;
  // prefix wildcards held by the session (e.g. session has "super_tenant.*", asks "super_tenant.impersonate_tenant")
  for (const held of eff) {
    if (held.endsWith(".*") && code.startsWith(held.slice(0, -1))) return true;
  }
  return false;
}

// ---------------- §2.6 server-side enforcement (PLAN-C1.3 F3) ----------------
// B5B §2.4: the UI gate (hasPermission) is an affordance only; the SERVER must enforce
// authorization on every request. RLS guards which ROWS a tenant sees; these primitives
// guard which VERBS a session may invoke — deny-by-default. Workers catch ForbiddenError
// (and UnauthorizedError) and translate to 403 / 401.

/** 401 — no/invalid session. */
export class UnauthorizedError extends Error {
  readonly status = 401;
  constructor(message = "unauthorized") {
    super(message);
    this.name = "UnauthorizedError";
  }
}

/** 403 — authenticated but not permitted. Carries the denied permission code for the audit log. */
export class ForbiddenError extends Error {
  readonly status = 403;
  readonly code: string;
  constructor(code: string, message?: string) {
    super(message ?? `forbidden: missing permission "${code}"`);
    this.name = "ForbiddenError";
    this.code = code;
  }
}

type AuthzSession = Pick<SympllexSession, "roleCodes"> & { permissionCodes?: string[]; mandateScope?: MandateScope; actingAsPartnerUserId?: string };

/** Enforce that the session holds `code`, else throw ForbiddenError. The unit of server-side
 *  authorization — call before every domain mutation/read. */
export function requirePermission(session: AuthzSession, aggregate: string, verb: Verb, modifier?: string): void {
  const code = permissionCode(aggregate, verb, modifier);
  if (!hasPermission(session, code)) throw new ForbiddenError(code);
}

/** Enforce a raw permission code (when the aggregate/verb are already composed). */
export function requirePermissionCode(session: AuthzSession, code: PermissionCode): void {
  if (!hasPermission(session, code)) throw new ForbiddenError(code);
}

/** Enforce that the session carries `roleCode` (coarse gate, e.g. super_tenant_admin). */
export function requireRole(session: Pick<SympllexSession, "roleCodes">, roleCode: string): void {
  if (!session.roleCodes.includes(roleCode)) throw new ForbiddenError(`role:${roleCode}`, `forbidden: requires role "${roleCode}"`);
}
